/*
 * Custom options importadas de Etsy.
 *
 * Resuelve dos cosas que Shopify no da hechas en el plan Basic:
 *
 * 1. FICHEROS. El `add.js` AJAX serializa las properties a JSON y descarta los
 *    <input type="file">. Cuando el producto tiene alguna pregunta de tipo fichero
 *    dejamos que el navegador haga el POST multipart de toda la vida, cortando el
 *    submit antes de que lo vea el tema.
 *
 * 2. RECARGOS. Una line item property no puede alterar el precio, y el `lineUpdate`
 *    de Cart Transform es exclusivo de Plus. Cuando el cliente contesta una pregunta
 *    con recargo se añade una segunda línea con la variante del producto oculto de
 *    add-ons, emparejada con la principal por la property `_bundle_id`.
 *
 * Si el producto no tiene ficheros ni recargos activos, este script no toca nada y el
 * tema se comporta como siempre (cart drawer incluido).
 *
 * Lo mantiene el sync (mirror/theme/): no lo edites en el editor de temas.
 */
(function () {
  'use strict';

  var PENDING_KEY = 'etsyPendingAddons';

  /*
   * Nombres de las properties visibles de la línea de recargo. Sin ellas, un carrito
   * con dos recargos son dos líneas «Opciones adicionales» idénticas y no hay forma de
   * saber cuál es de cuál —ni en el carrito ni en el pedido del admin, donde las
   * properties que empiezan por guion bajo tampoco se ven.
   */
  var NOTE_PRODUCT = 'Producto';
  var NOTE_OPTION = 'Opción';
  var CART_PATH = /\/cart(\/|$|\?)/;

  function warn(message, error) {
    if (window.console && console.warn) console.warn('[etsy-custom-options] ' + message, error || '');
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (text) {
          throw new Error(url + ' → ' + response.status + ' ' + text);
        });
      }
      return response.json();
    });
  }

  function bundleId() {
    return 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  /* Una pregunta con recargo solo cobra si el cliente la ha contestado. */
  function isAnswered(container) {
    var inputs = container.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (input.type === 'file') {
        if (input.files && input.files.length > 0) return true;
      } else if (String(input.value || '').trim() !== '') {
        return true;
      }
    }
    return false;
  }

  function activeAddons(root) {
    var addons = [];
    var product = root.getAttribute('data-addon-product') || '';
    var fields = root.querySelectorAll('[data-addon-variant]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      var variantId = parseInt(field.getAttribute('data-addon-variant'), 10);
      if (!variantId || !isAnswered(field)) continue;
      addons.push({
        variantId: variantId,
        label: field.getAttribute('data-addon-label') || '',
        product: product
      });
    }
    return addons;
  }

  function addonProperties(bundle, addon) {
    var properties = { _addon_for: bundle, _addon_label: addon.label };
    if (addon.product) properties[NOTE_PRODUCT] = addon.product;
    if (addon.label) properties[NOTE_OPTION] = addon.label;
    return properties;
  }

  function labelsOf(addons) {
    return addons.map(function (addon) {
      return addon.label;
    });
  }

  function prop(item, name) {
    return (item.properties && item.properties[name]) || '';
  }

  /*
   * ¿Es una línea del producto técnico de recargos? Se mira el SKU y no el handle
   * porque el handle es configurable y esto es un asset estático, sin Liquid delante.
   * Los SKU los pone `mirror/transform/options.py`: ADDON-<céntimos>.
   */
  function isAddonLine(item) {
    return String(item.sku || '').indexOf('ADDON-') === 0 || !!prop(item, '_addon_for');
  }

  function paidLabels(item) {
    var raw = prop(item, '_paid_options');
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /* Las properties del padre sin las opciones cuyo recargo ya no está en el carrito. */
  function withoutPaidOptions(item, drop, keep) {
    var next = {};
    var properties = item.properties || {};
    Object.keys(properties).forEach(function (name) {
      if (name === '_bundle_id' || name === '_paid_options') return; // se recolocan
      if (drop.indexOf(name) !== -1) return;
      next[name] = properties[name];
    });
    // `/cart/change.js` ignora un objeto de properties vacío —comprobado contra la
    // tienda—, así que siempre va al menos `_bundle_id`, vacío si ya no queda nada
    // cobrado. Empieza por guion bajo: no se ve ni en el carrito ni en el pedido.
    next._bundle_id = keep.length ? prop(item, '_bundle_id') : '';
    if (keep.length) next._paid_options = JSON.stringify(keep);
    return next;
  }

  function setHiddenProperty(form, name, value) {
    var selector = 'input[name="properties[' + name + ']"]';
    var input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      form.appendChild(input);
    }
    input.value = value;
  }

  function quantityOf(form) {
    var field = form.querySelector('[name="quantity"]');
    return Math.max(1, parseInt((field && field.value) || '1', 10) || 1);
  }

  /* ─── página de producto ──────────────────────────────────────────────── */

  function prepareForms() {
    var roots = document.querySelectorAll('[data-etsy-custom-options]');
    for (var i = 0; i < roots.length; i++) {
      var form = roots[i].closest('form[action*="/cart/add"]');
      if (!form) {
        warn('el snippet no está dentro del formulario de producto; revisa la instalación');
        continue;
      }
      // El snippet ya ha deshabilitado el campo para no regalarlo; esto solo deja
      // rastro de por qué, que si no habría que adivinarlo.
      var huerfanos = roots[i].querySelectorAll('[data-addon-missing]');
      for (var j = 0; j < huerfanos.length; j++) {
        warn(
          'la opción con recargo ' +
            huerfanos[j].getAttribute('data-addon-missing') +
            ' no tiene variante en shop.metafields.custom.etsy_sync_state.addons; ' +
            'campo deshabilitado. Lanza `mirror sync` y revisa ese metafield.'
        );
      }
      if (roots[i].querySelector('input[type="file"]')) {
        form.setAttribute('enctype', 'multipart/form-data');
        form.setAttribute('data-etsy-native-post', '');
      }
    }
  }

  /*
   * En captura sobre `document`, y registrado en cuanto se parsea este fichero.
   *
   * Los temas modernos delegan el submit en `document` con `capture: true` —Horizon
   * lo hace en component.js— y entre dos listeners del MISMO nodo y la MISMA fase
   * gana quien se registró antes. Por eso este script tiene que ir como script
   * clásico en el <head>: los del tema son módulos, que están diferidos, así que un
   * script clásico siempre se les adelanta.
   *
   * Y por eso se corta con `stopImmediatePropagation`: `stopPropagation` solo evita
   * que el evento siga a otros nodos, pero los demás listeners del mismo nodo se
   * ejecutan igual. Con el tema escuchando en `document` como nosotros, sería inútil.
   */
  function onSubmitCapture(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    var root = form.querySelector('[data-etsy-custom-options]');
    if (!root) return;

    var addons;
    try {
      addons = activeAddons(root);
    } catch (error) {
      warn('no se pudieron leer los recargos; se deja pasar el submit', error);
      return;
    }

    var nativePost = form.hasAttribute('data-etsy-native-post');
    if (!nativePost && addons.length === 0) return; // el tema sigue mandando

    event.stopImmediatePropagation(); // el tema ya no ve este submit

    var id = bundleId();
    if (addons.length) {
      setHiddenProperty(form, '_bundle_id', id);
      // El padre se lleva la lista de opciones suyas que van con cargo. Es lo que
      // permite deshacerlas si el cliente borra la línea del recargo en el carrito:
      // desde allí ya no hay forma de saber qué property venía cobrada.
      setHiddenProperty(form, '_paid_options', JSON.stringify(labelsOf(addons)));
    }

    if (nativePost) {
      // El navegador hace el POST multipart y aterriza en /cart, donde añadimos
      // el add-on: un POST clásico solo puede llevar una línea.
      if (addons.length) {
        try {
          sessionStorage.setItem(
            PENDING_KEY,
            JSON.stringify({ bundleId: id, addons: addons, quantity: quantityOf(form) })
          );
        } catch (error) {
          warn('sessionStorage no disponible; el recargo no se cobrará', error);
        }
      }
      return; // sin preventDefault: que envíe el navegador
    }

    event.preventDefault();
    addWithAddons(form, id, addons);
  }

  function addWithAddons(form, id, addons) {
    var data = new FormData(form);
    var properties = {};
    data.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    });

    var quantity = quantityOf(form);
    // Los recargos van PRIMERO en la petición: el carrito de Shopify lista lo último
    // añadido arriba del todo, así que mandándolos antes acaban justo debajo de su
    // producto, que es donde se leen bien.
    var items = addons.map(function (addon) {
      return { id: addon.variantId, quantity: quantity, properties: addonProperties(id, addon) };
    });
    items.push({ id: data.get('id'), quantity: quantity, properties: properties });

    postJSON('/cart/add.js', { items: items })
      .then(function () {
        window.location.href = '/cart';
      })
      .catch(function (error) {
        // Que un fallo nuestro no impida comprar: submit nativo sin recargo.
        warn('no se pudo añadir con recargo; se envía el formulario sin él', error);
        form.submit();
      });
  }

  /* ─── página de carrito ───────────────────────────────────────────────── */

  function flushPending() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY); // primero, para no encadenar recargas
    } catch (error) {
      return Promise.resolve(false);
    }
    if (!raw) return Promise.resolve(false);

    var pending;
    try {
      pending = JSON.parse(raw);
    } catch (error) {
      return Promise.resolve(false);
    }
    if (!pending || !pending.addons || !pending.addons.length) return Promise.resolve(false);

    var items = pending.addons.map(function (addon) {
      return {
        id: addon.variantId,
        quantity: pending.quantity || 1,
        properties: addonProperties(pending.bundleId, addon)
      };
    });

    return postJSON('/cart/add.js', { items: items })
      .then(function () {
        return true;
      })
      .catch(function (error) {
        warn('no se pudo añadir el recargo al carrito', error);
        return false;
      });
  }

  /*
   * Deja el carrito coherente en las dos direcciones. Se llama al abrir /cart y, como
   * última red, justo antes de salir hacia el checkout.
   *
   *   · Recargo sin producto padre  → fuera. Cubre las dos formas de llegar ahí: que
   *     el cliente borrase el producto, y que añadiese la variante por su cuenta desde
   *     la ficha del producto técnico. Un recargo suelto no significa nada.
   *   · Producto con la opción puesta y sin su recargo → se quita también la opción.
   *     El cliente ha dicho que no quiere pagar el extra; lo que no puede pasar es que
   *     se fabrique un grabado que nadie ha cobrado.
   *   · Cantidades distintas → el recargo sigue a su padre.
   */
  function reconcileCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        var parents = {};
        items.forEach(function (item) {
          var id = prop(item, '_bundle_id');
          if (id) parents[id] = item;
        });

        var changes = [];
        var cobradas = {}; // bundle → etiquetas que aún conservan su línea de recargo

        items.forEach(function (item) {
          if (!isAddonLine(item)) return;
          var target = prop(item, '_addon_for');
          var parent = target ? parents[target] : null;
          if (!parent) {
            changes.push({ id: item.key, quantity: 0 });
            return;
          }
          (cobradas[target] = cobradas[target] || {})[prop(item, '_addon_label')] = true;
          if (parent.quantity !== item.quantity) {
            changes.push({ id: item.key, quantity: parent.quantity });
          }
        });

        items.forEach(function (item) {
          var bundle = prop(item, '_bundle_id');
          if (!bundle) return;
          var pagadas = paidLabels(item);
          if (!pagadas.length) return;

          var vivas = cobradas[bundle] || {};
          var caidas = pagadas.filter(function (label) {
            return !vivas[label];
          });
          if (!caidas.length) return;

          var siguen = pagadas.filter(function (label) {
            return !!vivas[label];
          });
          changes.push({ id: item.key, properties: withoutPaidOptions(item, caidas, siguen) });
        });

        if (!changes.length) return false;
        return changes
          .reduce(function (chain, change) {
            return chain.then(function () {
              return postJSON('/cart/change.js', change);
            });
          }, Promise.resolve())
          .then(function () {
            return true;
          });
      })
      .catch(function (error) {
        warn('no se pudo reconciliar el carrito', error);
        return false;
      });
  }

  /* ─── anidado visual en el carrito ─────────────────────────────────────── */

  /*
   * El tema ya sabe pintar una línea colgando de otra: `snippets/cart-products.liquid`
   * le pone `cart-items__nested-line` a las filas con `item.parent_relationship`, que
   * es la relación que produce un Cart Transform —cosa de app, no la tenemos—. Como
   * todo lo que hace esa clase es CSS y depende de que la fila vaya justo detrás de su
   * padre, se le pone a mano y se coloca la fila en su sitio.
   *
   * Es solo aspecto. Si algo de aquí falla, el carrito sigue cobrando bien.
   */
  var NESTED_CLASS = 'cart-items__nested-line';
  var nestMap = {}; // key de la línea del recargo → key de la de su producto
  var nestSignature = '';
  var nestFetching = false;
  var nestLastFetch = 0;
  var nestTimer = null;

  function cartRows() {
    return document.querySelectorAll('[data-key][data-parent-key]');
  }

  function rowSignature(rows) {
    var keys = [];
    for (var i = 0; i < rows.length; i++) keys.push(rows[i].getAttribute('data-key'));
    return keys.sort().join(','); // ordenada: recolocar filas no cuenta como cambio
  }

  function nestingFromCart(cart) {
    var parents = {};
    var mapa = {};
    (cart.items || []).forEach(function (item) {
      var id = prop(item, '_bundle_id');
      if (id) parents[id] = item.key;
    });
    (cart.items || []).forEach(function (item) {
      if (!isAddonLine(item)) return;
      var padre = parents[prop(item, '_addon_for')];
      if (padre) mapa[item.key] = padre;
    });
    return mapa;
  }

  function applyNesting() {
    var rows = cartRows();
    for (var i = 0; i < rows.length; i++) {
      var fila = rows[i];
      var padreKey = nestMap[fila.getAttribute('data-key')];
      if (!padreKey || !fila.parentNode) continue;

      // El padre se busca entre sus hermanos: la página y el cajón lateral pintan las
      // mismas keys en dos sitios distintos y no hay que mezclarlos.
      var padre = null;
      var hermanos = fila.parentNode.children;
      for (var j = 0; j < hermanos.length; j++) {
        if (hermanos[j].getAttribute && hermanos[j].getAttribute('data-key') === padreKey) {
          padre = hermanos[j];
          break;
        }
      }
      if (!padre) continue;

      fila.classList.add(NESTED_CLASS);
      if (padre.nextElementSibling !== fila) {
        fila.parentNode.insertBefore(fila, padre.nextElementSibling);
      }
    }
  }

  function refreshNesting() {
    var rows = cartRows();
    var firma = rowSignature(rows);
    if (firma === nestSignature) {
      applyNesting(); // el tema ha repintado: basta con volver a marcarlas
      return;
    }

    var ahora = Date.now();
    if (nestFetching || ahora - nestLastFetch < 1000) return;
    nestFetching = true;
    nestLastFetch = ahora;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        nestMap = nestingFromCart(cart);
        nestSignature = firma;
        applyNesting();
      })
      .catch(function () {
        // Sin anidado, pero sin romper nada.
      })
      .then(function () {
        nestFetching = false;
      });
  }

  function watchCart() {
    try {
      refreshNesting();
      if (!window.MutationObserver) return;
      new MutationObserver(function () {
        window.clearTimeout(nestTimer);
        nestTimer = window.setTimeout(refreshNesting, 120);
      }).observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      warn('no se pudo anidar las líneas de recargo', error);
    }
  }

  /* ─── salida hacia el checkout ─────────────────────────────────────────── */

  /*
   * El carrito de este tema es un cajón lateral: se puede borrar una línea y pagar sin
   * pasar nunca por /cart, que es donde vive la reconciliación. Así que se reconcilia
   * también al pulsar «pagar». Si algo cambió, se manda a /cart en vez de al checkout:
   * el cliente tiene que ver el carrito corregido antes de pagarlo, no encontrarse otro
   * total en la pasarela.
   *
   * Todo aquí falla hacia abrir el checkout. Que un error nuestro impida comprar sería
   * mucho peor que cualquiera de los descuadres que esto arregla.
   */
  var CHECKOUT_TIMEOUT = 2000;
  var leavingToCheckout = false;

  function checkoutTrigger(event) {
    var node = event.target;
    if (event.type === 'submit') {
      // Solo el botón de pagar: este mismo formulario se envía también para
      // actualizar cantidades, y eso no hay que interceptarlo.
      var submitter = event.submitter;
      return submitter && submitter.name === 'checkout' ? submitter : null;
    }
    return node && node.closest ? node.closest('[name="checkout"]') : null;
  }

  function onCheckoutCapture(event) {
    if (leavingToCheckout) return;

    var trigger;
    try {
      trigger = checkoutTrigger(event);
    } catch (error) {
      return; // ante la duda, que pague
    }
    if (!trigger) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var resumed = false;
    function resume(changed) {
      if (resumed) return;
      resumed = true;
      if (changed) {
        window.location.assign('/cart');
        return;
      }
      leavingToCheckout = true;
      trigger.click();
    }

    var guard = window.setTimeout(function () {
      resume(false);
    }, CHECKOUT_TIMEOUT);

    reconcileCart()
      .then(function (changed) {
        window.clearTimeout(guard);
        resume(changed);
      })
      .catch(function () {
        window.clearTimeout(guard);
        resume(false);
      });
  }

  function initCart() {
    if (!CART_PATH.test(window.location.pathname)) return;
    flushPending().then(function (added) {
      return reconcileCart().then(function (changed) {
        if (added || changed) window.location.reload();
      });
    });
  }

  // Se registra YA, sin esperar al DOM: lo que importa es llegar antes que el tema.
  // El resto sí necesita el DOM montado.
  document.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener('click', onCheckoutCapture, true);
  document.addEventListener('submit', onCheckoutCapture, true);

  function init() {
    prepareForms();
    initCart();
    watchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
